import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Column,
  Columns,
  Inline,
  Link,
  Stack,
} from 'braid-design-system';

import type { ProfileCardView } from '../../../types/profile.types';
import { getAvatarDisplayProps } from '../../../utils/getAvatarDisplayProps';
import type { LoginPromptProps } from '../../GenericLoginPrompt/GenericLoginPrompt';
import { LastUpdated } from '../../ProfileView/LastUpdated/LastUpdated';
import { AvatarContacted } from '../AvatarContacted/AvatarContacted';
import { FakeWorkHistory } from '../FakeWorkHistory/FakeWorkHistory';
import { LastInteraction } from '../LastInteraction/LastInteraction';
import { Location } from '../Location/Location';
import { MaybeApproachableBadge } from '../MaybeApproachableBadge/MaybeApproachableBadge';
import { Name } from '../Name/Name';
import { Nationality } from '../Nationality/Nationality';
import { Salary } from '../Salary/Salary';
import { VerifiedCredentialBadge } from '../VerifiedCredentialBadge/VerifiedCredentialBadge';
import { WorkHistory } from '../WorkHistory/WorkHistory';

import translations from './.vocab';

interface Props<T extends ProfileCardView = ProfileCardView> {
  profile: T;
  isAuthenticated: boolean;
  loginUrl: string;
  profileViewLink: string;
  renderTalentPoolActions?: (profile: T) => React.ReactNode;
  renderActions?: (profile: T) => React.ReactNode;
  onProfileClick?: (profileSlug: string) => void;
  trackingCallbacks: LoginPromptProps['trackingCallbacks'];
  onLastInteractionClick?: (profileSlug: string) => void;
  onLoginNavigate?: (url: string) => void;
}

export const ProfileCard = <T extends ProfileCardView = ProfileCardView>({
  profile,
  isAuthenticated,
  loginUrl,
  profileViewLink,
  renderActions,
  renderTalentPoolActions,
  onProfileClick,
  trackingCallbacks,
  onLastInteractionClick,
  onLoginNavigate = (url) => {
    window.location.href = url;
  },
}: Props<T>) => {
  const { t } = useTranslations(translations);

  const {
    slug,
    approachability,
    displayName,
    digitalIdentity,
    hasVerifiedCredentials,
    workHistories,
    homeLocation,
    salary,
    nationalities,
    hasEthicalHiring,
    lastInteractionDuration,
    lastModifiedDurationLabel,
    profileAvatarUrls,
    hasConnectionHistory,
  } = profile;

  const shouldShowRealWorkHistories =
    workHistories?.length > 0 && isAuthenticated;

  const shouldShowFakeWorkHistories =
    workHistories?.length >= 1 && !isAuthenticated;

  const { photoUrl, variant } = getAvatarDisplayProps({
    hasEthicalHiring,
    imageUrl: profileAvatarUrls?.image128Url ?? undefined,
  });

  return (
    <Link
      href={profileViewLink}
      onClick={(e: React.MouseEvent) => {
        if (e.button === 0 && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          onProfileClick?.(slug);
        }
        // Right-click (button === 2) will work normally for context menu
      }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box paddingX={{ mobile: 'small', tablet: 'medium' }} paddingY="large">
        <Columns space="small">
          <Column width="content">
            <AvatarContacted
              hasBeenContacted={hasConnectionHistory}
              name={displayName}
              photoUrl={photoUrl}
              variant={variant}
              size="large"
            />
          </Column>
          <Column>
            <Stack space="large">
              <Box
                display="flex"
                width="full"
                gap="small"
                style={{
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                }}
              >
                <Box minWidth={0} style={{ flex: '1 1 auto' }}>
                  <Stack space="small">
                    <Name
                      hasEthicalHiring={Boolean(hasEthicalHiring)}
                      displayName={displayName}
                      digitalIdentity={digitalIdentity}
                      isSignedIn={isAuthenticated}
                    />

                    {shouldShowRealWorkHistories && (
                      <WorkHistory workHistories={workHistories} />
                    )}

                    {shouldShowFakeWorkHistories && (
                      <FakeWorkHistory realWorkHistory={workHistories[0]} />
                    )}
                    {approachability === 'high' || hasVerifiedCredentials ? (
                      <Inline space="xxsmall" alignY="center">
                        {approachability === 'high' && (
                          <MaybeApproachableBadge />
                        )}
                        {hasVerifiedCredentials && <VerifiedCredentialBadge />}
                      </Inline>
                    ) : null}
                  </Stack>
                </Box>
                {renderTalentPoolActions ? (
                  <Box flexShrink={0}>{renderTalentPoolActions(profile)}</Box>
                ) : null}
              </Box>

              <Stack space="xsmall">
                <Location homeLocation={homeLocation} />
                {nationalities ? (
                  <Nationality nationalities={nationalities} />
                ) : null}
                {salary ? <Salary salary={salary} /> : null}
              </Stack>

              {lastModifiedDurationLabel ||
              lastInteractionDuration ||
              renderActions ? (
                <Stack space="xsmall">
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap="medium"
                  >
                    {lastModifiedDurationLabel || lastInteractionDuration ? (
                      <Stack space="xsmall">
                        {lastModifiedDurationLabel ? (
                          <LastUpdated duration={lastModifiedDurationLabel} />
                        ) : null}
                        {lastInteractionDuration ? (
                          <LastInteraction
                            lastInteractionDuration={lastInteractionDuration}
                            onClick={() => onLastInteractionClick?.(slug)}
                          />
                        ) : null}
                      </Stack>
                    ) : null}

                    {renderActions ? (
                      <Box flexGrow={1}>
                        <Inline align="right" space="none">
                          {renderActions ? <>{renderActions(profile)}</> : null}
                        </Inline>
                      </Box>
                    ) : null}
                  </Box>
                </Stack>
              ) : null}

              {!isAuthenticated && (
                <Box maxWidth="xsmall">
                  <Button
                    tone="neutral"
                    variant="soft"
                    size="small"
                    onClick={(e) => {
                      // We can't have <a> tags within <a> tags (top level link)
                      // So we use a button instead, and tell it to not trigger the <a>
                      // parent above. This does mean you can't context-open the
                      // login link, but that's a small price to pay.
                      e.preventDefault();
                      e.stopPropagation();

                      trackingCallbacks.onLoginPressed({
                        component: {
                          name: 'sign-in_navigation',
                          type: 'textLinkButton',
                        },
                      });

                      onLoginNavigate(loginUrl);
                    }}
                  >
                    {t('SIGN_IN_FOR_FULL_WORK_HISTORY')}
                  </Button>
                </Box>
              )}
            </Stack>
          </Column>
        </Columns>
      </Box>
    </Link>
  );
};
